import { useEffect, useRef, useState } from 'react'
import { useClickOutside } from '@carrent/ui'
import { tasksApi } from '@carrent/api'

/**
 * UserPicker:
 * - single: selected={user} onChange={setUser}
 * - multi:  multi selected={[u1,u2]} onChange={setUsers}
 * - role:   "any"|"manager"|"user"
 */
export function UserPicker({ selected, onChange, multi = false, role = '', placeholder = 'Поиск...' }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  useClickOutside(ref, () => setOpen(false))

  useEffect(() => {
    if (!open) return
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await tasksApi.searchUsers(q, role, 10)
        let items = data.data || []
        if (multi && Array.isArray(selected)) {
          const ids = new Set(selected.map(u => u.id))
          items = items.filter(u => !ids.has(u.id))
        }
        setResults(items)
      } finally { setLoading(false) }
    }, 250)
    return () => clearTimeout(t)
  }, [q, open, role])

  // close on outside click

  const pick = user => {
    if (multi) {
      onChange([...(selected || []), user])
    } else {
      onChange(user)
    }
    setQ('')
    if (!multi) setOpen(false)
  }

  const remove = id => {
    if (multi) onChange(selected.filter(u => u.id !== id))
    else onChange(null)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Selected chips */}
      {multi ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
          {(selected || []).map(u => (
            <span key={u.id} className="chip chip--active" style={{ display: 'flex', gap: 4 }}>
              {u.full_name}
              <button onClick={() => remove(u.id)}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>×</button>
            </span>
          ))}
        </div>
      ) : selected ? (
        <div className="card" style={{ padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <strong style={{ fontSize: '0.88rem' }}>{selected.full_name}</strong>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{selected.phone}</div>
          </div>
          <button onClick={() => remove(selected.id)} className="btn-icon">×</button>
        </div>
      ) : null}

      {(multi || !selected) && (
        <input className="input" placeholder={placeholder}
               value={q} onChange={e => { setQ(e.target.value); setOpen(true) }}
               onFocus={() => setOpen(true)} />
      )}

      {open && (multi || !selected) && (
        <div className="card" style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          maxHeight: 240, overflowY: 'auto', marginTop: 4, padding: 0,
        }}>
          {loading ? (
            <div className="text-muted text-center" style={{ padding: 12 }}>Поиск...</div>
          ) : results.length === 0 ? (
            <div className="text-muted text-center" style={{ padding: 12 }}>Никого не найдено</div>
          ) : results.map(u => (
            <div key={u.id} onClick={() => pick(u)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong style={{ fontSize: '0.85rem' }}>{u.full_name}</strong>
                <span className="text-muted" style={{ fontSize: '0.7rem' }}>{u.role}</span>
              </div>
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>{u.phone}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
